import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { InfoCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Skeleton, Tooltip } from 'antd';
import { TrackerCustomEvent, useTracker } from 'common-package';
import { Dialog } from 'antd-mobile';
import noMachineImg from '@/assets/image/no-machine.png';

import servingAvatarImg from '@/assets/image/serving-avatar.png';
import { getAgeFromBirthday, transformVipName2Text } from '@/utils';

import OrderStateComp from '@/components/order-state';
import MemberDrawerComp from '@/components/member-drawer';
import service from '@/service';

import { BODY_PART, MassageType2Text, storagePrefixFixedV2 } from '@/constant';
import ScoreStar from '../ScoreStar';
import { handleServiceDuration } from '../ServiceRecordCard';
import SupplyForm from './SupplyForm';

import cx from './index.module.scss';

export const MEMBER_VIP = storagePrefixFixedV2 + '/image/member-vip.png';

const hasNullOrEmptyValue = obj => {
  for (let key in obj) {
    if (obj?.[key] === null || obj?.[key] === '') {
      return true;
    }
  }
  return false;
};

export interface ServiceRecordDrawerRefAttrs {
  show: (param?: string) => void;
  showHomePageDrawer: (orderId: string) => void;
}

export const TechniqueScore = ({
  robotTechScore,
  accuracyScore,
  onRobotTechScoreChange,
  onAccuracyScoreChange,
}: {
  robotTechScore: number;
  accuracyScore: number;
  onRobotTechScoreChange?: (score: number) => void;
  onAccuracyScoreChange?: (score: number) => void;
}) => {
  const handleRobotTechScoreChange = (score: number) => {
    !!onRobotTechScoreChange && onRobotTechScoreChange(score);
  };

  const handleAccuracyScoreChange = (score: number) => {
    !!onAccuracyScoreChange && onAccuracyScoreChange(score);
  };

  return (
    <div className={cx('technique-score')}>
      <div className={cx('score-line')}>
        <span className={cx('desc')}>手法评分</span>
        <ScoreStar score={robotTechScore} size="huge" widthEvaluate handleClick={handleRobotTechScoreChange} />
      </div>

      <div className={cx('score-line')}>
        <span className={cx('desc')}>位置精准度</span>
        <ScoreStar score={accuracyScore} size="huge" widthEvaluate handleClick={handleAccuracyScoreChange} />
      </div>
    </div>
  );
};

const ServiceRecordDrawer = forwardRef<
  ServiceRecordDrawerRefAttrs,
  {
    addClickGoToSupplyBtnTimes?: () => void;
    refreshList?: () => void;
  }
>((props, ref) => {
  const [open, setOpen] = useState(false);

  const [currentOrderId, setCurrentOrderId] = useState<string>();
  const [evaluateInfo, setEvaluateInfo] = useState<ServiceEvaluateInfoResponse>();
  const [serviceOrder, setServiceOrder] = useState<GetServiceOrderListResponse>();
  const [isAtSupplyForm, setIsAtSupplyForm] = useState(false);
  const [isShowGoToSupplyButton, setIsShowGoToSupplyButton] = useState(false);
  const [isAtHomePageDrawer, setIsAtHomePageDrawer] = useState(false);
  const [massageTaskInfo, setMassageTaskInfo] = useState<GetMassageTaskInfoResponse[]>([]);
  const [formatMassageTaskInfoMap, setFormatMassageTaskInfoMap] = useState({
    manualMassage: { duration: null },
    massage: { duration: null },
  });
  const { sendEvent } = useTracker();

  // 判断是否展示跳转补充信息Form的按钮
  const judgeIsNeedShowGoToSupplyButton = (info: ServiceEvaluateInfo, orderData: GetServiceOrderListResponse) => {
    // 人工单无法补充
    if (orderData?.onlyManual) {
      return false;
    }

    // 由于备注信息加入，店员可以一直补充备注信息则一直返回true
    return true;
  };

  const isShowMachineTime = useMemo(() => !serviceOrder?.onlyManual, [serviceOrder]);

  const isShowManualTime = useMemo(() => {
    if (!['Shockwave', 'RadioFrequency'].includes(serviceOrder?.productItems?.[0])) {
      return false;
    }
    // 关闭了人工治疗选项且非人工单
    if (massageTaskInfo?.length === 1 && !serviceOrder?.onlyManual) {
      return false;
    }
    return true;
  }, [massageTaskInfo, serviceOrder]);

  useImperativeHandle(
    ref,
    () => ({
      show: item => {
        setOpen(true);
        setCurrentOrderId(item);
      },
      showHomePageDrawer: orderId => {
        setOpen(true);
        setCurrentOrderId(orderId);
        setIsAtSupplyForm(true);
        setIsAtHomePageDrawer(true);
      },
    }),
    []
  );

  useEffect(() => {
    // 记录抽屉展示次数
    if (open && sendEvent) {
      sendEvent(TrackerCustomEvent.ServiceRecordDrawerAlertShow, {
        et: 'EXP',
      });
    }
  }, [open, sendEvent]);

  useEffect(() => {
    const orderId = currentOrderId;
    if (open && orderId) {
      Promise.all([
        service.serviceRecordApi.getEvaluateInfoByOrder(orderId),
        service.massage.getMassageTaskInfo(`${orderId}`),
        service.service.getServiceOrder(`${orderId}`),
      ]).then(([evaluateInfo, massageTaskInfo, serviceOrder]) => {
        setEvaluateInfo({
          ...evaluateInfo.data,
          serviceOrderId: orderId,
        });
        setServiceOrder(serviceOrder.data);
        setIsShowGoToSupplyButton(judgeIsNeedShowGoToSupplyButton(evaluateInfo.data, serviceOrder.data));
        setMassageTaskInfo(massageTaskInfo?.data);
        setFormatMassageTaskInfoMap(obj => {
          massageTaskInfo.data.forEach(item => {
            if (item.massageType === 'ManualMassage') {
              obj['manualMassage'].duration = item.massageDuration ?? null;
            } else {
              obj['massage'].duration = item.massageDuration ?? null;
            }
          });

          return obj;
        });
      });
    }
    return () => {
      setFormatMassageTaskInfoMap({
        manualMassage: { duration: null },
        massage: { duration: null },
      });
    };
  }, [currentOrderId, isAtSupplyForm, open]);

  return (
    <MemberDrawerComp
      isShowCloseIcon={!isAtHomePageDrawer}
      maskClosable={!isAtHomePageDrawer && !isAtSupplyForm}
      drawerStyle={{ background: '#E6E8EB', height: '95%', borderRadius: '0', padding: '0 30px 30px 30px' }}
      isShowTime={true}
      mask={true}
      width={512}
      open={open}
      handleClose={() => {
        const handle = () => {
          // 弹窗关闭默认清空数据
          setEvaluateInfo(null);
          setIsAtHomePageDrawer(false);
          setIsAtSupplyForm(false);
          setOpen(false);
          isAtSupplyForm && props.refreshList && props.refreshList();
        };

        // 在补充页关闭会多一个确认弹窗
        if (isAtSupplyForm) {
          Dialog.confirm({
            title: '确认退出评价吗？',
            content: '确认后将退出评价',
            confirmText: '确认',
            onConfirm: async () => handle(),
          });
        } else {
          handle();
        }
      }}
    >
      {!isAtSupplyForm ? (
        !!serviceOrder && !!evaluateInfo ? (
          <div className={cx('container')} id="service-record-drawer">
            <div className={cx('header')}>
              <div
                className={cx('header-left')}
                style={{
                  backgroundImage: `url(${serviceOrder?.userAvatar || servingAvatarImg})`,
                }}
              >
                <img src={MEMBER_VIP} alt="vip" className={cx('member-vip')} />
              </div>

              <div className={cx('header-right')}>
                <div className={cx('header-right-box')}>
                  <div className={cx('user-info')}>
                    <div>{serviceOrder?.customerName}</div>
                    <div className={cx('line')}></div>
                    <div>
                      {(serviceOrder?.age || getAgeFromBirthday(serviceOrder?.birthday ?? '2000/01/01')) + '岁'}
                    </div>
                  </div>
                  {isShowGoToSupplyButton && (
                    <div
                      className={cx('serving-text')}
                      onClick={() => {
                        setIsAtSupplyForm(true);
                        props?.addClickGoToSupplyBtnTimes && props?.addClickGoToSupplyBtnTimes();
                        sendEvent(TrackerCustomEvent.GoServiceRecordDrawerSupplyButton, {
                          et: 'CLK',
                        });
                      }}
                    >
                      {'去补充 '}
                      <RightOutlined />
                    </div>
                  )}
                </div>
                {serviceOrder?.userVipName && (
                  <div className={cx('card-info', transformVipName2Text(`${serviceOrder?.userVipName}`))}>
                    {serviceOrder?.userVipName}
                  </div>
                )}
              </div>
            </div>

            {/* 订单基础信息 */}
            <div className={cx('card')}>
              <div className={cx('header')}>
                <div className={cx('title')}>{serviceOrder?.productName}</div>
                {serviceOrder?.state && <OrderStateComp state={serviceOrder?.state} />}
              </div>
              <div>
                <div className={cx('line')}>
                  {' '}
                  {BODY_PART[serviceOrder?.bodyPart]}｜
                  {serviceOrder?.productItems.map(item => `${MassageType2Text[item]} `)}
                </div>
                <div className={cx('line')}> 机器人：{serviceOrder?.deviceName}</div>
                <div className={cx('line')}>服务时间：{serviceOrder?.createTime}</div>
              </div>
            </div>

            {/* 治疗时长 */}
            <div className={cx('card')}>
              <div className={cx('title')}>治疗时长</div>
              <div className={cx('content')}>
                {isShowMachineTime && (
                  <Tooltip title="实际理疗时间">
                    <div className={cx('line')}>
                      <span className={cx('line-title')}>
                        <InfoCircleOutlined style={{ marginRight: 4 }} />
                        机器治疗
                      </span>
                      <span className={cx('text')}>
                        {formatMassageTaskInfoMap.massage?.duration !== null
                          ? handleServiceDuration(formatMassageTaskInfoMap.massage?.duration)
                          : '-'}
                      </span>
                    </div>
                  </Tooltip>
                )}
                {isShowManualTime && (
                  <Tooltip title="实际理疗时间">
                    <div className={cx('line')}>
                      <span className={cx('line-title')}>
                        <InfoCircleOutlined style={{ marginRight: 4 }} />
                        人工治疗
                      </span>
                      <span className={cx('text')}>
                        {formatMassageTaskInfoMap.manualMassage?.duration !== null
                          ? handleServiceDuration(formatMassageTaskInfoMap.manualMassage?.duration)
                          : '-'}
                      </span>
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>

            {/* 更多信息 */}
            <div className={cx('card')}>
              <div className={cx('title')}>更多信息</div>
              <div className={cx('content')}>
                <div className={cx('line')}>
                  <span className={cx('line-title')}>订单来源</span>
                  <span className={cx('text')}>{evaluateInfo?.source ?? '-'}</span>
                </div>

                <div className={cx('line')}>
                  <span className={cx('line-title')}>订单金额</span>
                  <span className={cx('text')}>{evaluateInfo?.amount?.toFixed(2) ?? '-'}</span>
                </div>

                <div className={cx('line')}>
                  <span className={cx('line-title')}>备注</span>
                  <span className={cx('text')}>{evaluateInfo?.remark ?? '-'}</span>
                </div>
              </div>
            </div>

            {/* 机器人评分 */}
            <div className={cx('card')}>
              <div className={cx('title')}>机器人评分</div>
              {/* 纯人工方案不展示评分 */}
              {evaluateInfo?.robotTechScore >= 0 && evaluateInfo?.accuracyScore >= 0 && !serviceOrder.onlyManual ? (
                <TechniqueScore
                  robotTechScore={evaluateInfo?.robotTechScore ?? 0}
                  accuracyScore={evaluateInfo?.accuracyScore ?? 0}
                />
              ) : (
                <div className={cx('score-empty')}>
                  <img src={noMachineImg} alt="no-machine" />
                  <p>暂无评分</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={cx('container')}>
            <Skeleton.Input active />
          </div>
        )
      ) : (
        <SupplyForm
          isAmountNecessary={!isAtHomePageDrawer}
          evaluateInfo={evaluateInfo}
          cancelCallback={(needCloseDrawer?: boolean) => {
            setIsAtSupplyForm(false);
            needCloseDrawer && setOpen(false);
          }}
        />
      )}
    </MemberDrawerComp>
  );
});

export default ServiceRecordDrawer;
